import React, { useState, useEffect } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { useToast } from '../hooks/use-toast';
import LoadingSpinner from './LoadingSpinner';
import { CreditCard, AlertCircle } from 'lucide-react';

const PAYPAL_CLIENT_ID = "AZcdYduGFfmpbNpuqvtAzVWW0DMX6JNkLbMD4BG0DKjCXfCGCTmJkicimLfeeMMNObU9S-y4tlplkOJI";

const PayPalCheckout = ({ 
  orderData, 
  onSuccess, 
  onError, 
  onCancel,
  disabled = false 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { toast } = useToast();

  // PayPal SDK options
  const paypalOptions = {
    "client-id": PAYPAL_CLIENT_ID,
    currency: "EUR",
    intent: "capture",
    locale: "it_IT"
  };

  // Create PayPal order
  const createOrder = async (data, actions) => {
    try {
      setIsLoading(true);
      setError(null);

      // Call our backend to create PayPal order
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/paypal/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: orderData.items.map(item => ({
            name: item.name || 'Prodotto',
            price: item.unitPrice || item.price || 0,
            quantity: item.quantity || 1,
            description: item.description || ''
          })),
          total: orderData.total,
          customer_email: orderData.customerEmail,
          order_id: orderData.orderId,
          return_url: `${window.location.origin}/checkout/success`,
          cancel_url: `${window.location.origin}/checkout/cancel`
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore nella creazione dell\'ordine PayPal');
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Errore nella creazione dell\'ordine PayPal');
      }

      return result.paypal_order_id;
      
    } catch (error) {
      console.error('Error creating PayPal order:', error);
      setError(error.message);
      toast({
        title: "Errore PayPal",
        description: error.message,
        variant: "destructive"
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Handle successful payment
  const onApprove = async (data, actions) => {
    try {
      setIsLoading(true);
      setError(null);

      // Call our backend to capture the payment
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/paypal/capture-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order_id: orderData.orderId,
          paypal_order_id: data.orderID
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore nella cattura del pagamento');
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Errore nella cattura del pagamento');
      }

      // Success! Call parent success handler
      toast({
        title: "Pagamento completato!",
        description: "Il tuo ordine è stato elaborato con successo.",
        variant: "default"
      });

      if (onSuccess) {
        onSuccess({
          paypalOrderId: data.orderID,
          captureId: result.capture_id,
          amount: result.amount,
          currency: result.currency,
          status: result.status
        });
      }

    } catch (error) {
      console.error('Error capturing PayPal payment:', error);
      setError(error.message);
      
      toast({
        title: "Errore nel pagamento",
        description: error.message,
        variant: "destructive"
      });

      if (onError) {
        onError(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle payment cancellation
  const onCancelHandler = (data) => {
    console.log('PayPal payment cancelled:', data);
    
    toast({
      title: "Pagamento annullato",
      description: "Il pagamento PayPal è stato annullato.",
      variant: "default"
    });

    if (onCancel) {
      onCancel(data);
    }
  };

  // Handle payment errors
  const onErrorHandler = (error) => {
    console.error('PayPal payment error:', error);
    setError(error.message || 'Errore nel pagamento PayPal');
    
    toast({
      title: "Errore PayPal",
      description: error.message || 'Si è verificato un errore durante il pagamento.',
      variant: "destructive"
    });

    if (onError) {
      onError(error);
    }
  };

  if (!orderData || !orderData.items || orderData.items.length === 0) {
    return (
      <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
        <AlertCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
        <p className="text-gray-500">Nessun prodotto nel carrello</p>
      </div>
    );
  }

  return (
    <div className="paypal-checkout-container">
      {/* PayPal Button Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <CreditCard className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">
            Paga con PayPal
          </h3>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-600">Totale:</p>
          <p className="text-xl font-bold text-purple-600">
            €{orderData.total?.toFixed(2)}
          </p>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {isLoading && (
        <div className="relative">
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
            <LoadingSpinner />
          </div>
        </div>
      )}

      {/* PayPal Buttons */}
      <PayPalScriptProvider options={paypalOptions}>
        <PayPalButtons
          style={{
            layout: "vertical",
            color: "blue",
            shape: "rect",
            label: "paypal",
            height: 45,
            tagline: false
          }}
          createOrder={createOrder}
          onApprove={onApprove}
          onCancel={onCancelHandler}
          onError={onErrorHandler}
          disabled={disabled || isLoading}
          forceReRender={[orderData.total, orderData.items?.length]}
        />
      </PayPalScriptProvider>

      {/* PayPal Info */}
      <div className="mt-4 text-xs text-gray-500 text-center">
        <p>
          Pagamento sicuro elaborato da PayPal. I tuoi dati di pagamento sono protetti con crittografia SSL.
        </p>
      </div>
    </div>
  );
};

export default PayPalCheckout;